import { z } from "zod";

import { machineTypeSchema } from "../machine-type";
import { machineWithCorrugateTracksSchema } from "../machine-with-corrugate-tracks";

const fusionPhysicalMachineSettingsSchema = z
	.object({
		crossHead: z
			.object({
				maximumPosition: z.number(),
			})
			.loose(),
	})
	.loose();

export const fusionMachineSchema = machineWithCorrugateTracksSchema.extend({
	machineType: z.literal(machineTypeSchema.enum.Fusion),
	physicalMachineSettings: fusionPhysicalMachineSettingsSchema.nullable(),
	waitForReleaseTrigger: z.boolean().optional(),
});

// TODO @eb remove this function when the Fusion aligns with our decided upon schemas
export const prepareFusionMachineData = (data: unknown): unknown => {
	if (typeof data !== "object" || data === null) {
		return data;
	}

	const input = data as Record<string, unknown>;
	const securePrintConfigurationInput = input.securePrintConfiguration as { delayLabelRetrieval?: boolean } | undefined;

	const iotDevice = {
		deviceId: input.iotDeviceId ?? "",
		deviceName: input.iotEdgeDeviceId ?? "",

		hubName: input.iotHubName ?? "",
		moduleId: input.iotEdgeModuleId ?? "",
		moduleVersion: input.communicatorVersion ?? "",
		lastProofOfLife: input.proofOfLifeUpdated,
	};

	let tracks = input.tracks;
	if (Array.isArray(tracks)) {
		tracks = tracks.map((t) => {
			if (typeof t !== "object" || t === null) {
				return t;
			}

			const track = t as Record<string, unknown>;

			let assignedMaterial = track.loadedCorrugate;
			if (typeof assignedMaterial === "object" && assignedMaterial !== null) {
				const material = assignedMaterial as Record<string, unknown>;

				assignedMaterial = {
					id: material.id ?? "",
					alias: material.alias ?? "",
					width: material.width ?? 0,
					thickness: material.thickness ?? 0,
					// TODO @eb are we looking at quality or brand here? The payload has both
					quality: material.quality ?? 0,
				};
			}

			return {
				...track,
				trackNumber: track.number,
				isEmpty: track.isTrackEmpty,
				assignedMaterial,
			};
		});
	}

	const error = input.error as Record<string, unknown>;
	let stopReason: {
		stopCode: number;
		message: string;
		arguments: number[];
		isError: boolean;
	} = { stopCode: 0, message: "", arguments: [], isError: false };
	if (typeof error === "object" && error !== null) {
		stopReason = {
			stopCode: (error.code as number) ?? 0,
			message: (error.message as string) ?? "",
			arguments: Array.isArray(error.arguments) ? error.arguments.map(Number) : [],
			isError: error.code !== 0,
		};
	}

	let workstations = input.workstations;
	if (!Array.isArray(workstations) || workstations.length < 1) {
		workstations = [
			{
				number: 1,
				alarms:
					stopReason.stopCode > 0
						? [{ code: stopReason.stopCode, message: stopReason.message, arguments: stopReason.arguments.map(String) }]
						: [],
				warnings: [],
				stopReason: {
					code: stopReason.stopCode,
					message: stopReason.message,
					arguments: stopReason.arguments.map(String),
					isError: stopReason.isError,
				},
			},
		];
	}

	return {
		...input,
		plcVersion: input.plcVersion ?? "",
		machineType: "Fusion",
		iotDevice,
		stopReason,
		activeWarnings: [],
		tracks,
		securePrintConfiguration: {
			delayLabelRetrieval: securePrintConfigurationInput?.delayLabelRetrieval ?? false,
		},
		workstations,
	};
};

export type FusionMachine = z.infer<typeof fusionMachineSchema>;
